// Previsão & IA Preditiva - Risco de não fechar no prazo
import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Loader2, TrendingDown, AlertTriangle, Sparkles, Clock } from 'lucide-react';
import { DnaTableV4 } from '@/design-system/DnaDesignSystem';

export function PrevisaoFechamento({ year, trimestre }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/pendencias-academicas/previsao-fechamento?year=${year}&trimestre=${encodeURIComponent(trimestre)}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [year, trimestre]);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-purple-600" /></div>;
  }
  if (!data) return null;

  const { regua, kpis, sugestoes, previsoes } = data;

  return (
    <div className="space-y-6" data-testid="previsao-fechamento">
      {/* Régua de Tempo */}
      <div className="bg-gradient-to-r from-indigo-50 via-white to-purple-50 border border-indigo-100 rounded-2xl p-5">
        <div className="flex items-center gap-3 mb-3">
          <Clock className="w-5 h-5 text-indigo-600" />
          <h3 className="font-bold text-slate-800">Régua de Tempo — {trimestre}</h3>
          <Badge variant="outline" className="ml-auto text-xs">
            {regua.dias_restantes} dias restantes
          </Badge>
        </div>
        <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all"
            style={{ width: `${regua.pct_tempo}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-slate-500 mt-1">
          <span>{regua.dias_decorridos}/{regua.dias_totais} dias decorridos ({regua.pct_tempo}%)</span>
          <span>Fim: {regua.data_fim}</span>
        </div>
      </div>

      {/* KPIs de Risco */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total Previsões" value={kpis.total_previsoes} color="purple" />
        <KpiCard label="Risco ALTO" value={kpis.risco_alto} color="red" icon={<TrendingDown className="w-5 h-5" />} />
        <KpiCard label="Risco MÉDIO" value={kpis.risco_medio} color="amber" icon={<AlertTriangle className="w-5 h-5" />} />
        <KpiCard label="Risco BAIXO" value={kpis.risco_baixo} color="emerald" />
      </div>

      {/* Sugestões da IA */}
      {sugestoes && sugestoes.length > 0 && (
        <div className="bg-white border border-purple-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h3 className="font-bold text-slate-800">Sugestões Personalizadas da IA</h3>
            <Badge className="bg-purple-100 text-purple-700 text-xs ml-auto">{sugestoes.length}</Badge>
          </div>
          <div className="space-y-2">
            {sugestoes.map((s, i) => (
              <div
                key={`${s.professor_id}-${s.tipo_avaliacao}-${i}`}
                className="flex items-start gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50"
                data-testid={`sugestao-${i}`}
              >
                <div
                  className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                  style={{
                    backgroundColor: s.prioridade === 'critico' ? '#EF4444'
                      : s.prioridade === 'urgente' ? '#F97316' : '#F59E0B'
                  }}
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">{s.mensagem}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {s.turmas.join(', ')}{s.turmas.length < s.turmas_count ? '…' : ''} · {s.total_pendente} pendência(s)
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className="text-[10px] uppercase"
                  style={{
                    color: s.prioridade === 'critico' ? '#EF4444' : s.prioridade === 'urgente' ? '#F97316' : '#F59E0B',
                    borderColor: 'currentColor'
                  }}
                >
                  {s.prioridade}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Previsões detalhadas */}
      <DnaTableV4
        stickyHeader
        title="Previsão por Turma × Disciplina × Avaliação"
        subtitle="Ordenado por gap (atraso vs tempo)"
        testId="ia-pendencias-previsao-table-v4"
      >
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-600">
            <tr>
              <th className="text-left px-4 py-2">Turma</th>
              <th className="text-left px-4 py-2">Disciplina</th>
              <th className="text-left px-4 py-2">Tipo</th>
              <th className="text-left px-4 py-2">Professor</th>
              <th className="text-center px-4 py-2">Progresso</th>
              <th className="text-center px-4 py-2">Gap</th>
              <th className="text-center px-4 py-2">Vel/dia</th>
              <th className="text-center px-4 py-2">Risco</th>
            </tr>
          </thead>
          <tbody>
            {previsoes.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-slate-400">Nenhuma previsão de risco</td></tr>
            ) : previsoes.map((p, i) => (
              <tr key={`${p.turma_id}-${p.disciplina_nome}-${p.tipo_avaliacao}-${i}`}>
                <td className="px-4 py-2 font-medium">{p.turma_nome}</td>
                <td className="px-4 py-2 text-slate-600">{p.disciplina_nome}</td>
                <td className="px-4 py-2 text-slate-600 text-xs">{p.tipo_avaliacao}</td>
                <td className="px-4 py-2 text-slate-600 text-xs">{p.professor_nome}</td>
                <td className="px-4 py-2 text-center font-mono">{p.progresso_atual}%</td>
                <td className="px-4 py-2 text-center font-mono">
                  <span style={{ color: p.cor }}>{p.gap > 0 ? `+${p.gap}` : p.gap}</span>
                </td>
                <td className="px-4 py-2 text-center font-mono text-xs">
                  {isFinite(p.velocidade_necessaria_dia) ? p.velocidade_necessaria_dia : '∞'}
                </td>
                <td className="px-4 py-2 text-center">
                  <Badge style={{ backgroundColor: p.cor + '22', color: p.cor, border: `1px solid ${p.cor}55` }}>
                    {p.risco}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DnaTableV4>
    </div>
  );
}

function KpiCard({ icon, label, value, color }) {
  const cls = {
    red: 'bg-red-50 text-red-700 border-red-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200'
  }[color];
  return (
    <div className={`rounded-xl border p-4 ${cls}`}>
      <div className="flex items-center gap-2 mb-1 opacity-80">
        {icon}<span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

export default PrevisaoFechamento;
